// The original's 2D UI system (IP2, cIP2_2DScene / cIP2_2DP and subclasses) for the browser:
// the converted UI data (docs/platform/AssetConv.md "ui/"), the scene tree of a .bkm document,
// placement in the 1920x1080 design space and the event timelines. Drawing: draw.ts. The Python
// reference of the same rules is re/tools/formats/ui.py; differences are noted where the
// browser port follows the original's code instead (text placement: FM_Font).
import { assetJson, assetUrl } from '../../assets.ts';
import type { DecodeJob, DecodeResult, Pixels } from './decode.worker.ts';
import { replaceControlTokens } from './controls.ts';

export const DESIGN_W = 1920, DESIGN_H = 1080;

// XID types (re/tools/formats/ui.py XID_TYPES) of the drawn and animated classes.
export const T_MENU = 7002, T_POLY = 7003, T_TEX = 7004, T_TXT = 7005, T_TICK = 7013, T_BUTTON = 7014,
  T_TAB = 7015, T_DIALOG = 7025, T_WPCELL = 7027, T_CRAFT = 7029, T_XOM = 7019;
const ANI_ACTIVATE = 8001, ANI_COLOUR = 8004, ANI_COLOURSINGLE = 8005, ANI_EXIT = 8006, ANI_GOTOTIME = 8008, ANI_MOVE = 8009,
  ANI_SCALE = 8012, ANI_SEND = 8013, ANI_SETCOLOUR = 8015, ANI_SETCOLOURSINGLE = 8016, ANI_SETPOS = 8018,
  ANI_SETSCALE = 8020, ANI_PLAY_XOMANIMATION = 8042, ANI_HASXOMFINISHED = 8047;

export const xidType = (x: number): number => (x >>> 16) & 0xffff;
export const xidIndex = (x: number): number => x & 0xffff;

/** `Gui::TextLibImplCalcHash` 0xd7c5e0: h = 5381, h = h * 33 ^ c over the UTF-8 bytes, chars
 * sign-extended; the text tables are keyed by its 8 hex digits. */
export function guiHash(s: string): string {
  let h = 5381;
  for (const b of new TextEncoder().encode(s)) h = (Math.imul(h, 33) ^ (b >= 0x80 ? b - 256 : b)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

export type Rgba = [number, number, number, number];
type Sprite = { page: string; rect: number[]; crop: number[] };
type PageInfo = { png: string };
type FontRow = { file: string; font: string; index: number; px: number };
export type StyleState = { texture?: string; fontcolour?: number[]; scale?: number; showtitle?: boolean; align?: string };
/** A UI sprite (an atlas sprite or a standalone texture) as its own decoded bitmap: its size and
 * the packer's crop (x, y, original w, original h; cRM_Texture::UpdatePrimSubForCrop 0xfcc770);
 * `key` names it (its ui_textures path). */
export type TexRef = { img: ImageBitmap; sx: number; sy: number; sw: number; sh: number; crop: number[] | null; key: string };
/** Where a texture's pixels are: its page (or standalone) PNG and the sprite's rectangle there
 * (null: the whole standalone image). */
type TexSource = { key: string; png: string; rect: [number, number, number, number] | null; crop: number[] | null };

/** The decoder workers (decode.worker.ts) and their jobs in flight: a job goes to an idle one, a
 * new one while there are fewer than DECODERS, else the least busy. A 2048x2048 page takes
 * 0.2-0.4 s of script, so with two the front-end art's pages and a backdrop's textures (or the
 * HUD's pages) decode in parallel; at most two, the sim's worker boots meanwhile. */
const decoders: { worker: Worker; busy: number }[] = [];
const DECODERS = Math.max(1, Math.min(2, (navigator.hardwareConcurrency || 2) - 1));
let nextJob = 0;
const jobs = new Map<number, (r: DecodeResult) => void>();

/** The sprites `rects` of the PNG at `png` (an asset path) as RGBA pixels, decoded and cropped by
 * a decoder worker (decode.worker.ts); null for an empty rectangle or a failed decode. `UiData`
 * makes bitmaps of them, the HTML pages' art (ui/feart.ts) PNG files, the front-end backdrop
 * (render/meshes.ts `decodeInWorker`) data textures. */
export function decodePixels(png: string, rects: TexSource['rect'][]): Promise<(Pixels | null)[]> {
  let d = decoders.reduce<(typeof decoders)[number] | null>((m, x) => (!m || x.busy < m.busy ? x : m), null);
  if (!d || (d.busy > 0 && decoders.length < DECODERS)) {
    d = { worker: new Worker(new URL('./decode.worker.ts', import.meta.url), { type: 'module' }), busy: 0 };
    d.worker.onmessage = (ev: MessageEvent<DecodeResult>) => {
      const done = jobs.get(ev.data.id);
      jobs.delete(ev.data.id);
      done?.(ev.data);
    };
    decoders.push(d);
  }
  const id = nextJob++;
  const w = d;
  w.busy++;
  const pixels = new Promise<DecodeResult>(ok => {
    jobs.set(id, r => { w.busy--; ok(r); });
    w.worker.postMessage({ id, url: new URL(assetUrl(png), location.href).href, rects } satisfies DecodeJob);
  });
  return pixels.then(r => {
    if (r.error) console.warn('ui texture', r.error);
    return rects.map((_, i) => r.sprites[i] ?? null);
  });
}

/** The UI tables: sprites, pages, the reslist's TEXTURE table, fonts, strings, style guide. */
export class UiData {
  private sprites: Record<string, Sprite> = {};
  private pages: Record<string, PageInfo> = {};
  private standalone: Record<string, PageInfo> = {};
  private texTable: Record<string, { file: string; dir?: string }> = {};
  private meshTable: Record<string, { file: string; dir?: string }> = {};
  private fonts = new Map<number, { family: string; px: number }>();
  private defaultFont = 1000;
  strings: Record<string, string> = {};
  styles: Record<string, Record<string, Record<string, StyleState>>> = {};
  /** Decoded sprites by key (null: the decode failed), the decodes in flight and the sprites
   * waiting for their page's next decode (`request`), and the references that resolved. */
  private decoded = new Map<string, TexRef | null>();
  private pending = new Map<string, Promise<void>>();
  private queue = new Map<string, { src: TexSource; ok: () => void }[]>();
  private refs = new Map<number | string, TexRef>();

  static async load(lang = 'Eng'): Promise<UiData> {
    const d = new UiData();
    const [sp, rl, fo, tx, sg] = await Promise.all([
      assetJson<{ sprites: Record<string, Sprite>; pages: Record<string, PageInfo>; standalone: Record<string, PageInfo> }>('ui/sprites.json'),
      assetJson<{ tables: [string, Record<string, { file: string; dir?: string }>][] | Record<string, Record<string, { file: string; dir?: string }>> }>('ui/reslist.json'),
      assetJson<{ default: number; table: FontRow[] }>('ui/fonts.json'),
      assetJson<{ strings: Record<string, string> }>(`ui/text/${lang}.json`),
      assetJson<{ classes: Record<string, Record<string, Record<string, StyleState>>> }>('ui/styleguide.json'),
    ]);
    d.sprites = sp.sprites;
    d.pages = sp.pages;
    d.standalone = sp.standalone;
    const tables = Array.isArray(rl.tables) ? Object.fromEntries(rl.tables) : rl.tables;
    d.texTable = tables.TEXTURE ?? {};
    d.meshTable = tables.MESH ?? {};
    d.strings = tx.strings;
    d.styles = sg.classes;
    d.defaultFont = fo.default;
    // FM_FontManager::AddFont (QtShell_UI::Initialise): one FreeType face per file, pixel sizes in
    // the 1920x1080 design space.
    const faces = new Map<string, string>();
    for (const r of fo.table) {
      let family = faces.get(r.file);
      if (!family) {
        family = `wmd-ui-${faces.size}`;
        faces.set(r.file, family);
        const ff = new FontFace(family, `url("${assetUrl(r.file)}")`);
        document.fonts.add(ff);
      }
      d.fonts.set(r.index, { family, px: r.px });
    }
    await Promise.all([...document.fonts].filter(f => f.family.startsWith('wmd-ui-')).map(f => f.load().catch(() => undefined)));
    return d;
  }

  /** `cIP2_2DP::GetLocalisedString` -> the localiser `UI_LocaliserFunc` 0xa9f090:
   * `Gui::TextLibFind(token)` with its `%Ctrl_..%` tokens replaced (controls.ts); the item's own
   * string when the token is missing. */
  localise(token: string | null | undefined, fallback: string | null | undefined): string {
    if (token) {
      const s = this.strings[guiHash(token)];
      if (s !== undefined) return replaceControlTokens(s, k => this.text(k));
    }
    return fallback ?? '';
  }

  /** The text of a string table token (`Gui::TextLibFind`), or null. */
  text(token: string): string | null {
    return this.strings[guiHash(token)] ?? null;
  }

  font(fontXid: number | null | undefined): { family: string; px: number } {
    const idx = fontXid ? xidIndex(fontXid) : this.defaultFont;
    return this.fonts.get(idx) ?? this.fonts.get(this.defaultFont) ?? { family: 'sans-serif', px: 20 };
  }

  /** A reslist TEXTURE resource (XID or index) or a file name -> its sprite, drawn from its own
   * ImageBitmap; null until the decoder worker has made it (the first request starts that).
   * Decoding on the main thread stalled the page: WebKit decodes an image when it is first
   * drawn, and again after its memory cache dropped the decoded data of an image not painted
   * for a while. That cost 300-1000 ms per 2048x2048 page, at the HUD's first frame and at
   * every show of the hot seat panel or the weapons panel. A bitmap keeps its pixels. */
  texture(ref: number | string): TexRef | null {
    const hit = this.refs.get(ref);
    if (hit) return hit;
    const src = this.source(ref);
    if (!src) return null;
    const t = this.decoded.get(src.key);
    if (t) this.refs.set(ref, t);
    else if (t === undefined) void this.request(src);
    return t ?? null;
  }

  private source(ref: number | string): TexSource | null {
    const name = typeof ref === 'number' ? this.texTable[String(xidType(ref) ? xidIndex(ref) : ref)]?.file : ref;
    if (!name) return null;
    const key = `uidata/ui_textures/${name.toLowerCase()}`;
    const s = this.sprites[key];
    if (s) {
      const png = this.pages[s.page]?.png;
      const [x, y, w, h] = s.rect;
      return png ? { key, png, rect: [x, y, w, h], crop: s.crop && s.crop.some(v => v !== 0) ? s.crop : null } : null;
    }
    const png = this.standalone[key]?.png;
    return png ? { key, png, rect: null, crop: null } : null;
  }

  /** The sprite's decode: the sprites of one page requested in the same task share a decode of
   * the page. */
  private request(src: TexSource): Promise<void> {
    let p = this.pending.get(src.key);
    if (!p) {
      p = new Promise<void>(ok => {
        let q = this.queue.get(src.png);
        if (!q) {
          q = [];
          this.queue.set(src.png, q);
          queueMicrotask(() => this.flush(src.png));
        }
        q.push({ src, ok });
      });
      this.pending.set(src.key, p);
    }
    return p;
  }

  private flush(png: string): void {
    const q = this.queue.get(png) ?? [];
    this.queue.delete(png);
    // The bitmaps are made here, on the main thread that draws and releases them (a cheap copy of
    // a sprite's pixels).
    void decodePixels(png, q.map(x => x.src.rect))
      .then(sprites => Promise.all(sprites.map(s => (s ? createImageBitmap(new ImageData(s.data, s.w, s.h)).catch(() => null) : null))))
      .then(bitmaps => q.forEach(({ src, ok }, i) => {
        const b = bitmaps[i];
        this.decoded.set(src.key, b ? { img: b, sx: 0, sy: 0, sw: b.width, sh: b.height, crop: src.crop, key: src.key } : null);
        ok();
      }));
  }

  /** A reslist MESH resource (an XOM item's `m_ResID`) -> its xDesc, as the xom index names it
   * ("DataLinux/Objects/UI/<file>"; ui_xom/ is DataLinux/Objects/UI), or null. */
  mesh(ref: number): string | null {
    const file = this.meshTable[String(xidType(ref) ? xidIndex(ref) : ref)]?.file;
    return file ? `DataLinux/Objects/UI/${file}` : null;
  }

  /** The sprites of `refs` decoded (for the first frame they show in). */
  async preload(refs: (number | string)[]): Promise<void> {
    const sources = refs.map(r => this.source(r)).filter(s => s !== null);
    await Promise.all(sources.map(s => (this.decoded.has(s.key) ? undefined : this.request(s))));
  }

  /** The style guide state of a widget class/style (styleguide.txt). */
  style(cls: string, style: string | null | undefined, state: string): StyleState | null {
    const c = this.styles[cls];
    const st = c?.[style ?? 'Normal'] ?? c?.Normal;
    return st?.[state] ?? null;
  }

  /** Every state's texture of a widget class/style (what `style` may pick), for preloading. */
  styleTextures(cls: string, style: string | null | undefined): string[] {
    const c = this.styles[cls];
    const st = c?.[style ?? 'Normal'] ?? c?.Normal;
    return Object.values(st ?? {}).flatMap(s => (s.texture ? [s.texture] : []));
  }
}

/** A 2-D affine transform as a row-vector matrix (XMatrix4 convention, p' = p * M):
 * (x, y) -> (x a + y c + e, x b + y d + f). */
export type Mat = [number, number, number, number, number, number];
const IDENT: Mat = [1, 0, 0, 1, 0, 0];
export function matMul(A: Mat, B: Mat): Mat {
  return [A[0] * B[0] + A[1] * B[2], A[0] * B[1] + A[1] * B[3], A[2] * B[0] + A[3] * B[2], A[2] * B[1] + A[3] * B[3],
    A[4] * B[0] + A[5] * B[2] + B[4], A[4] * B[1] + A[5] * B[3] + B[5]];
}

type Step = Record<string, unknown> & { aniType: number; m_time_start?: number; m_length?: number };
type EventDef = { type: number; name: string; steps: Step[]; end: number };
/** A running handler: `t` the time of its last `animate` (`m_time_offset`), `held` while a
 * HASXOMFINISHED step holds it (`IPE2D_ARET_NO_CONTINUE`: the event's time stays at the step). */
type Running = { ev: EventDef; t: number; done: Set<number>; held: boolean };

/** One component (cIP2_2DP) of a scene (cIP2_2DScene::EditorLoad 0xf53550). */
export class Item {
  key = 0; id = 0; type = 0; name = ''; parentId = 0; layer = 10;
  x = 0; y = 0; w = 0; h = 0; sx = 1; sy = 1; angle = 0; lockX = 0; lockY = 0;
  active = true; normalBlend = true; inheritPos = true; inheritScale = true; inheritRot = true; inheritColour = true;
  colours: Rgba[] = [[1, 1, 1, 1], [1, 1, 1, 1], [1, 1, 1, 1], [1, 1, 1, 1]];
  resId = 0; style: string | null = null;
  sections: Record<string, Record<string, unknown>> = {};
  events = new Map<number, EventDef>();
  running: Running[] = [];
  children: Item[] = [];
  parent: Item | null = null;
  /** Runtime text (`cIP2_2DTxt::SetString`), replacing the authored string. */
  text: string | null = null;
  /** Runtime texture (`cIP2_2DP::SetResID` / a file name). */
  tex: number | string | null = null;
  /** Style-guide state of a widget (Focused, NotFocused, ...). */
  state: string | null = null;
  /** Generated children (weapons panel cells): extra draw data. */
  extra: Record<string, unknown> | null = null;
  /** An XOM item's current animation (cIP2_2DXom::m_CurrentAnimationID or the default one): a
   * clip name or a 0-based index into the mesh's clip list, whether it loops, and the seconds
   * since it started (`m_AnimTime`, advanced by the UI clock). */
  xomAnim: string | number | null = null;
  xomLoop = false;
  xomTime = 0;
  /** The current animation started since the last `animate`: cIP2_2DXom::__UpdateMesh's first
   * update after a change has a zero delta (m_LastTime -999), so its time starts one frame
   * later (match-end-A: the logo wipe played at frame 5218 reads 0 at 5219, 0.5 at 5249). */
  xomFresh = true;
  // placement (Scene.place)
  world: Mat = IDENT; pw = 0; ph = 0; pActive = true; pColours: Rgba[] = this.colours; pAngle = 0;
}

function itemFromJson(key: number, it: Record<string, any>): Item {
  const g = it.general ?? {};
  const i = new Item();
  i.key = key;
  i.id = Number(g.m_id);
  i.type = xidType(i.id);
  i.name = g.m_Name ?? '';
  i.parentId = Number(g.m_ParentID ?? 0);
  i.layer = Number(g.m_Layer ?? 10);
  const r = g.m_Rect ?? {};
  [i.x, i.y, i.w, i.h] = [Number(r.x ?? 0), Number(r.y ?? 0), Number(r.w ?? 0), Number(r.h ?? 0)];
  i.sx = Number(g.m_Scale?.x ?? 1);
  i.sy = Number(g.m_Scale?.y ?? 1);
  i.angle = Number(g.m_Angle ?? 0);
  i.lockX = Number(g.m_SideLockX ?? 0);
  i.lockY = Number(g.m_SideLockY ?? 0);
  const f = it.flags ?? {};
  i.active = f.m_bActive ?? true;
  i.normalBlend = f.m_bNormalBlend ?? true;
  i.inheritPos = f.m_bInheritPos ?? true;
  i.inheritScale = f.m_bInheritScale ?? true;
  i.inheritRot = f.m_bInheritRotation ?? true;
  i.inheritColour = f.m_bInheritColour ?? true;
  const c = g.m_Colours ?? {};
  i.colours = [0, 1, 2, 3].map(n => {
    const d = c[String(n)] ?? {};
    return [d.r ?? 1, d.g ?? 1, d.b ?? 1, d.a ?? 1] as Rgba;
  });
  i.resId = Number(g.m_ResID ?? 0);
  i.style = g.m_Style ?? null;
  for (const [k, v] of Object.entries(it)) if (k !== 'general' && k !== 'flags' && k !== 'm_pEventSystem') i.sections[k] = v as Record<string, unknown>;
  xomDefault(i);
  for (const [k, ev] of Object.entries<any>(it.m_pEventSystem?.list ?? {})) {
    // cIP2_2DEvent::Animations_Process sorts the animations by start time (IPE2D_Event_Sorter).
    const steps = Object.entries<Step>(ev.list ?? {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(e => e[1])
      .sort((a, b) => Number(a.m_time_start ?? 0) - Number(b.m_time_start ?? 0));
    const type = Number(ev.m_event_type ?? k);
    const end = Math.max(0, ...steps.map(s => Number(s.m_time_start ?? 0) + Number(s.m_length ?? 0)));
    i.events.set(type, { type, name: ev.m_editor_name ?? '', steps, end });
  }
  return i;
}

/** An XOM item's animation as cIP2_2DXom starts it: `m_DefaultAnimName` (looping per
 * `m_bDefaultLooping`) from time 0, or none. */
export function xomDefault(i: Item): void {
  const xom = i.sections.xom as { m_DefaultAnimName?: string; m_bDefaultLooping?: boolean } | undefined;
  i.xomAnim = xom?.m_DefaultAnimName || null;
  i.xomLoop = !!xom?.m_bDefaultLooping;
  i.xomTime = 0;
  i.xomFresh = true;
}

/** The velocity profile of ANI_MOVE / ANI_COLOUR (cIP2_2DAni_Move): accelerate over the first
 * m_max_vel_in_time of the length, decelerate after m_max_vel_out_time; the travelled fraction
 * at frame t of length L. */
export function profile(t: number, L: number, a: number, b: number): number {
  if (L <= 0 || t >= L) return 1;
  if (t <= 0) return 0;
  const ta = Math.trunc(L * a), tb = Math.trunc(b * L);
  const span = L - (ta + (L - tb)) * 0.5;
  if (span <= 0) return t / L;
  const v = 1 / span;
  if (t < ta) return 0.5 * v * t * t / ta;
  const p1 = 0.5 * v * ta;
  if (t < tb) return p1 + v * (t - ta);
  const p2 = p1 + v * (tb - ta), dl = L - tb;
  return dl > 0 ? Math.min(1, p2 + v * (t - tb) - 0.5 * v * (t - tb) ** 2 / dl) : 1;
}

function vec4(d: unknown, n: number): Rgba {
  const v = d as Record<string, Record<string, number>> | undefined;
  return [v?.x?.[n] ?? 1, v?.y?.[n] ?? 1, v?.z?.[n] ?? 1, v?.w?.[n] ?? 1];
}

/** A loaded .bkm scene: items, tree, placement, events. */
export class Scene {
  items: Item[] = [];
  roots: Item[] = [];
  private byName = new Map<string, Item>();
  private byKey = new Map<number, Item>();
  private byId = new Map<number, Item>();

  static async load(menu: string): Promise<Scene> {
    const doc = await assetJson<{ scene: { list: Record<string, unknown> } }>(`ui/menus/${menu}.bkm.json`);
    return new Scene(doc);
  }

  constructor(doc: { scene: { list: Record<string, unknown> } }) {
    for (const k of Object.keys(doc.scene.list).sort((a, b) => Number(a) - Number(b))) {
      const it = itemFromJson(Number(k), doc.scene.list[k] as Record<string, unknown>);
      this.add(it);
    }
    for (const i of this.items) {
      const p = this.byId.get(i.parentId);
      if (p && p !== i) {
        i.parent = p;
        p.children.push(i);
      } else this.roots.push(i);
    }
    this.initialState();
  }

  private add(i: Item): void {
    this.items.push(i);
    if (!this.byName.has(i.name)) this.byName.set(i.name, i);
    this.byKey.set(i.key, i);
    this.byId.set(i.id, i);
  }

  /** A generated child (cIP2_2DScene::CreateComponent at run time). */
  addChild(parent: Item, i: Item): Item {
    i.parent = parent;
    i.parentId = parent.id;
    parent.children.push(i);
    this.items.push(i);
    return i;
  }

  /** `cIP2_2DScene::ComponentGet(name)`: the first item of that name. */
  get(name: string): Item {
    const i = this.byName.get(name);
    if (!i) throw new Error(`ip2: no item ${name}`);
    return i;
  }

  find(name: string): Item | null {
    return this.byName.get(name) ?? null;
  }

  /** The item `name` below `root` (names repeat across the scene, e.g. TXT_PlayerName_01). */
  under(root: Item, name: string): Item | null {
    for (const c of root.children) {
      if (c.name === name) return c;
      const d = this.under(c, name);
      if (d) return d;
    }
    return null;
  }

  /** State the runtime sets before the first draw (re/tools/formats/ui.py
   * initial_runtime_state): dialogs closed, unselected tab pages hidden, items of other
   * platforms filtered (cIP2_2DScene::Filter_Items 0xf56350). */
  private initialState(): void {
    for (const i of this.items) {
      if (i.type === T_DIALOG) i.active = false;
      const tb = i.sections.TabButton as Record<string, unknown> | undefined;
      if (tb?.m_TabMenuID && !tb.m_bFirstTab) {
        const page = this.byId.get(Number(tb.m_TabMenuID));
        if (page) page.active = false;
      }
      if (/\[(ps4|ps3|xbox|xbone|xb360|nx|osx|ios)\]/i.test(i.name) && !/\[pc\]/i.test(i.name)) i.active = false;
    }
  }

  /** `cIP2_2DP::SendEventMessage` → `cIP2_2DEventSys::Trigger` 0xf400b0: (re)starts the item's
   * handler of `message` (an IPE2D_MESSAGE) at time 0 without processing it; the scene's next
   * `animate` (`cIP2_2DEvent::Animations_Process`) processes time 0, as the original's ProcessLogic
   * after OnIdle does. */
  trigger(i: Item, message: number): void {
    const ev = i.events.get(message);
    if (!ev) return;
    i.running = i.running.filter(r => r.ev.type !== message);
    i.running.push({ ev, t: -1, done: new Set(), held: false });
  }

  /** Whether the item's handler of `message` is still running. */
  isRunning(i: Item, message: number): boolean {
    return i.running.some(r => r.ev.type === message);
  }

  /** The time a running handler's last `animate` processed (`cIP2_2DEvent::m_last_time`), -1
   * before its first; -1 once it ended (the original resets `m_last_time` to 0 then). */
  runTime(i: Item, message: number): number {
    return i.running.find(r => r.ev.type === message)?.t ?? -1;
  }

  /** Advances every running event by `frames` (60 per second, cIP2_Time) and every XOM item's
   * animation clock (cIP2_2DXom::__UpdateMesh: m_AnimTime += the scene time's delta). A held
   * event keeps its time. */
  animate(frames: number): void {
    for (const i of this.items) {
      if (i.type === T_XOM) { if (i.xomFresh) i.xomFresh = false; else i.xomTime += frames / 60; }
      if (!i.running.length) continue;
      for (const r of [...i.running]) {
        if (!r.held) r.t += frames;
        this.step(i, r, r.t);
        if (r.t >= r.ev.end) i.running = i.running.filter(x => x !== r);
      }
    }
  }

  /** `cIP2_2DAni_Exit::Process`: an EXIT step took flow link `output` of the item's menu. */
  onExit: ((i: Item, output: number) => void) | null = null;
  /** `cIP2_2DXom::HasCurrentAnimationFinished` 0xf6dd90 of an XOM item: a non-looping current
   * animation whose time reached its duration (false without the mesh). */
  xomFinished: ((i: Item) => boolean) | null = null;

  private step(i: Item, r: Running, t: number): void {
    for (let n = 0; n < r.ev.steps.length; n++) {
      const s = r.ev.steps[n];
      const t0 = Number(s.m_time_start ?? 0), L = Number(s.m_length ?? 0);
      if (t < t0) continue;
      const k = profile(t - t0, L, Number(s.m_max_vel_in_time ?? 0), Number(s.m_max_vel_out_time ?? 1));
      const once = !r.done.has(n);
      switch (s.aniType) {
        case ANI_MOVE: {
          if (!r.done.has(n) && t - t0 >= L) r.done.add(n);
          const x0 = Number(s.m_startx ?? i.x), y0 = Number(s.m_starty ?? i.y);
          i.x = x0 + (Number(s.m_destx ?? x0) - x0) * k;
          i.y = y0 + (Number(s.m_desty ?? y0) - y0) * k;
          break;
        }
        case ANI_SETPOS:
          if (once) { r.done.add(n); i.x = Number(s.m_destx ?? s.m_x ?? i.x); i.y = Number(s.m_desty ?? s.m_y ?? i.y); }
          break;
        case ANI_COLOUR:
          for (let c = 0; c < 4; c++) {
            const a = vec4({ x: s.m_startx, y: s.m_starty, z: s.m_startz, w: s.m_startw }, c);
            const b = vec4({ x: s.m_destx, y: s.m_desty, z: s.m_destz, w: s.m_destw }, c);
            i.colours[c] = [0, 1, 2, 3].map(j => a[j] + (b[j] - a[j]) * k) as Rgba;
          }
          break;
        case ANI_COLOURSINGLE: {
          const a: Rgba = [Number(s.m_startx ?? 1), Number(s.m_starty ?? 1), Number(s.m_startz ?? 1), Number(s.m_startw ?? 1)];
          const b: Rgba = [Number(s.m_destx ?? 1), Number(s.m_desty ?? 1), Number(s.m_destz ?? 1), Number(s.m_destw ?? 1)];
          const col = [0, 1, 2, 3].map(j => a[j] + (b[j] - a[j]) * k) as Rgba;
          i.colours = [col, [...col] as Rgba, [...col] as Rgba, [...col] as Rgba];
          break;
        }
        case ANI_SETCOLOUR:
          if (once) {
            r.done.add(n);
            for (let c = 0; c < 4; c++) i.colours[c] = vec4({ x: s.m_startx, y: s.m_starty, z: s.m_startz, w: s.m_startw }, c);
          }
          break;
        case ANI_SETCOLOURSINGLE:
          if (once) {
            r.done.add(n);
            const col: Rgba = [Number(s.m_startx ?? 1), Number(s.m_starty ?? 1), Number(s.m_startz ?? 1), Number(s.m_startw ?? 1)];
            i.colours = [col, [...col] as Rgba, [...col] as Rgba, [...col] as Rgba];
          }
          break;
        case ANI_SCALE: {
          const x0 = Number(s.m_startx ?? i.sx), y0 = Number(s.m_starty ?? i.sy);
          i.sx = x0 + (Number(s.m_destx ?? x0) - x0) * k;
          i.sy = y0 + (Number(s.m_desty ?? y0) - y0) * k;
          break;
        }
        case ANI_SETSCALE:
          if (once) { r.done.add(n); i.sx = Number(s.m_destx ?? s.m_startx ?? i.sx); i.sy = Number(s.m_desty ?? s.m_starty ?? i.sy); }
          break;
        case ANI_ACTIVATE:
          if (once) {
            r.done.add(n);
            const tgt = this.byKey.get(Number(s.m_receiver ?? -1));
            const st = Number(s.m_state ?? 0);
            if (tgt && (st === 1 || st === 2)) tgt.active = st === 1;
          }
          break;
        case ANI_SEND:
          // cIP2_2DAni_Send: SendEventMessage to the receiver, the sender itself included (the
          // results' Fade_Poly sends itself "Fade To Transparent" on creation). The trigger only
          // restarts the handler, so a self-send cannot recurse.
          if (once) {
            r.done.add(n);
            const tgt = this.byKey.get(Number(s.m_receiver ?? -1));
            if (tgt) this.trigger(tgt, Number(s.m_message ?? 0));
          }
          break;
        case ANI_GOTOTIME:
          if (once) {
            r.done.add(n);
            r.t = Number(s.m_time ?? 0);
            r.done.clear();
          }
          break;
        case ANI_PLAY_XOMANIMATION:
          // cIP2_2DAni_PlayXomAni::Process 0xf9b610 + cIP2_2DXom::SetCurrentAnimation 0xf6dd50:
          // anim (m_AniID index - 1) of the mesh's list, m_bLooping, from time 0.
          if (once) {
            r.done.add(n);
            const id = Number(s.m_AniID ?? 0);
            if (i.type === T_XOM && xidType(id) && xidIndex(id)) {
              i.xomAnim = xidIndex(id) - 1;
              i.xomLoop = !!s.m_bLooping;
              i.xomTime = 0;
              i.xomFresh = true;
            }
          }
          break;
        case ANI_HASXOMFINISHED:
          // cIP2_2DAni_HasXomFinished::Process 0xfb34a0: until the owner's animation finished the
          // event stops here (NO_CONTINUE: m_last_time = m_time_offset = the step's start, the
          // later steps wait); then its time jumps to the step's end and the frame's later steps
          // still compare against the frame's old time (Animations_Process's local copy).
          if (once && i.type === T_XOM) {
            if (!(this.xomFinished?.(i) ?? false)) {
              r.held = true;
              r.t = t0;
              return;
            }
            r.done.add(n);
            r.held = false;
            r.t = t0 + L;
          }
          break;
        case ANI_EXIT:
          if (once) {
            r.done.add(n);
            this.onExit?.(i, Number(s.m_output_index ?? 0));
          }
          break;
        default:
          break;
      }
    }
  }

  /** Placement of every item (cIP2_2DP::Update 0xf7efe0): local = T(origin) S R(angle)
   * T(int x, int y), world = local * parent; the origin follows the side locks
   * (cIP2_2DP::GetOrigin 0xf7e220); colours multiply down the tree with m_bInheritColour. */
  place(): void {
    const walk = (items: Item[], parent: Mat, parentActive: boolean, parentCols: Rgba[] | null): void => {
      for (const it of items) {
        let ox = 0, oy = 0;
        if (it.lockX || it.lockY) {
          if (it.lockY === 1) oy = it.h * 0.5;
          else if (it.lockY === 2) oy = -it.h * 0.5;
          if (it.lockX === 1) ox = Math.trunc(it.w * 0.5);
          else if (it.lockX === 2) ox = Math.trunc(-it.w * 0.5);
        }
        const a = (it.angle * Math.PI) / 180, cos = Math.cos(a), sin = Math.sin(a);
        const local = matMul(matMul([it.sx * cos, it.sx * sin, -it.sy * sin, it.sy * cos, 0, 0], IDENT), [1, 0, 0, 1, Math.trunc(it.x), Math.trunc(it.y)]);
        const withOrigin: Mat = [local[0], local[1], local[2], local[3], ox * local[0] - oy * local[2] + local[4], ox * local[1] - oy * local[3] + local[5]];
        let pm = parent;
        if (!(it.inheritPos && it.inheritScale && it.inheritRot)) {
          const m = [...parent] as Mat;
          if (!it.inheritScale || !it.inheritRot) {
            const n0 = Math.hypot(m[0], m[1]) || 1, n1 = Math.hypot(m[2], m[3]) || 1;
            m[0] /= n0; m[1] /= n0; m[2] /= n1; m[3] /= n1;
            if (!it.inheritRot) { m[0] = 1; m[1] = 0; m[2] = 0; m[3] = 1; }
          }
          if (!it.inheritPos) { m[4] = 0; m[5] = 0; }
          pm = m;
        }
        const world = matMul(withOrigin, pm);
        it.world = world;
        const sx = Math.hypot(world[0], world[1]), sy = Math.hypot(world[2], world[3]);
        it.pw = it.w * sx;
        it.ph = it.h * sy;
        it.pAngle = (Math.atan2(world[1], world[0]) * 180) / Math.PI;
        it.pActive = parentActive && it.active;
        it.pColours = parentCols && it.inheritColour ? it.colours.map((c, n) => c.map((v, j) => v * parentCols[n][j]) as Rgba) : it.colours;
        walk(it.children, world, it.pActive, it.pColours);
      }
    };
    walk(this.roots, IDENT, true, null);
  }
}
